/**
 * Console Log Buffer
 *
 * Intercepts console.log/warn/error/debug/info and stores entries in a
 * ring buffer so the AI agent can retrieve them via `get_console_logs`.
 *
 * Playwright-style entry shape: type, text, timestamp.
 */

export interface ConsoleEntry {
    /** Message type — mirrors console method names */
    type: 'log' | 'warn' | 'error' | 'debug' | 'info';
    /** Concatenated text of all arguments */
    text: string;
    /** Unix timestamp in ms */
    timestamp: number;
}

const MAX_ENTRIES = 500;
const entries: ConsoleEntry[] = [];
let installed = false;

/** Install the console interceptor. Safe to call multiple times. */
export function installConsoleCapture(): void {
    if (installed) return;
    installed = true;

    const methods = ['log', 'warn', 'error', 'debug', 'info'] as const;
    for (const method of methods) {
        const original = console[method].bind(console);
        console[method] = (...args: unknown[]) => {
            push(method, args);
            original(...args);
        };
    }
}

function push(type: ConsoleEntry['type'], args: unknown[]): void {
    const text = args.map(a => {
        if (typeof a === 'string') return a;
        // Errors JSON.stringify to "{}" — surface message + stack instead, so
        // React's "Maximum update depth" component stack is retrievable.
        if (a instanceof Error) return `${a.message}\n${a.stack ?? ''}`;
        try { return JSON.stringify(a); }
        catch { return String(a); }
    }).join(' ');

    entries.push({ type, text, timestamp: Date.now() });

    // Trim ring buffer
    if (entries.length > MAX_ENTRIES) {
        entries.splice(0, entries.length - MAX_ENTRIES);
    }
}

/** Get buffered entries. Supports tail (last N) or index range. */
export function getEntries(opts?: {
    tail?: number;
    offset?: number;
    limit?: number;
    type?: ConsoleEntry['type'];
}): ConsoleEntry[] {
    let result = entries;

    if (opts?.type) {
        result = result.filter(e => e.type === opts.type);
    }

    if (opts?.tail != null) {
        result = result.slice(-opts.tail);
    } else if (opts?.offset != null || opts?.limit != null) {
        const start = opts?.offset ?? 0;
        const end = opts?.limit != null ? start + opts.limit : undefined;
        result = result.slice(start, end);
    }

    return result;
}

/** Clear all buffered entries. */
export function clearEntries(): void {
    entries.length = 0;
}

/** Total number of entries currently buffered. */
export function entryCount(): number {
    return entries.length;
}
